/**
 * ZAdminUsers - Zod Validation Schemas for Admin Users API
 *
 * Schemas for Clerk ↔ Nexus DB user synchronization.
 * Mirrors the organizations sync pattern but for users.
 *
 * Status values:
 * - "synced": User exists in both Clerk and DB with matching email
 * - "missing": User exists in Clerk but not in DB
 * - "email_mismatch": User exists in both but emails differ
 */

import { z } from "zod";

import { ApiSuccessSchema } from "../../common/response.schema";

// ═══════════════════════════════════════════════════════════════
// SHARED SCHEMAS
// ═══════════════════════════════════════════════════════════════

const ClerkUserIdSchema = z
  .string()
  .min(1, "Clerk user ID is required")
  .regex(/^user_/, "Clerk user ID must start with 'user_'");

const UserSyncStatusSchema = z.enum(["synced", "missing", "email_mismatch"]);

// ═══════════════════════════════════════════════════════════════
// RESPONSE SCHEMAS
// ═══════════════════════════════════════════════════════════════

export const UserOrgMembershipSchema = z.object({
  id: z.string(),
  name: z.string()
});

export const UserSyncInfoSchema = z.object({
  /** Clerk user ID */
  clerkId: z.string(),
  /** Full name from Clerk (firstName + lastName) */
  clerkName: z.string(),
  /** Primary email in Clerk */
  clerkEmail: z.string(),
  /** Whether the user exists in Nexus DB */
  inDb: z.boolean(),
  /** Email in Nexus DB (null if not in DB) */
  dbEmail: z.string().nullable(),
  /** Current sync status */
  status: UserSyncStatusSchema,
  /** Organizations this user belongs to (up to 20, from DB) */
  organizations: z.array(UserOrgMembershipSchema),
  /** When the user was created in Clerk (ISO 8601) */
  createdAt: z.string().datetime()
});

export const UserSyncSummarySchema = z.object({
  /** Total users in Clerk */
  clerkTotal: z.number().int().min(0),
  /** Total users in Nexus DB */
  dbTotal: z.number().int().min(0),
  /** Users that are fully synced */
  synced: z.number().int().min(0),
  /** Users missing from Nexus DB */
  missing: z.number().int().min(0),
  /** Users with email mismatches */
  emailMismatch: z.number().int().min(0)
});

export const UserSyncStatusResponseSchema = z.object({
  summary: UserSyncSummarySchema,
  users: z.array(UserSyncInfoSchema)
});

export const SyncUserResponseSchema = z.object({
  success: z.boolean(),
  message: z.string()
});

// ═══════════════════════════════════════════════════════════════
// REQUEST SCHEMAS
// ═══════════════════════════════════════════════════════════════

export const SyncUserPathVarsSchema = z.object({
  clerkUserId: ClerkUserIdSchema
});

// ═══════════════════════════════════════════════════════════════
// ZADMINUSERS MANIFEST
// ═══════════════════════════════════════════════════════════════

export const ZAdminUsers = {
  /**
   * Get sync status between Clerk and Nexus DB for users
   * GET /api/admin/users/sync-status
   */
  GetUserSyncStatus: {
    method: "GET" as const,
    path: "/api/admin/users/sync-status",
    Response: ApiSuccessSchema(UserSyncStatusResponseSchema)
  },

  /**
   * Sync a single user from Clerk
   * POST /api/admin/users/sync/:clerkUserId
   */
  SyncUser: {
    method: "POST" as const,
    path: "/api/admin/users/sync/:clerkUserId",
    PathVars: SyncUserPathVarsSchema,
    Response: ApiSuccessSchema(SyncUserResponseSchema)
  }
} as const;

// ═══════════════════════════════════════════════════════════════
// TYPE EXPORTS
// ═══════════════════════════════════════════════════════════════

/** Sync status for an individual user */
export type UserSyncStatus = z.infer<typeof UserSyncStatusSchema>;

/** Single org membership on a user sync row */
export type UserOrgMembership = z.infer<typeof UserOrgMembershipSchema>;

/** Individual user sync information */
export type UserSyncInfo = z.infer<typeof UserSyncInfoSchema>;

/** Summary statistics for user sync status */
export type UserSyncSummary = z.infer<typeof UserSyncSummarySchema>;

/** Complete user sync status response */
export type UserSyncStatusResponse = z.infer<typeof UserSyncStatusResponseSchema>;

/** Path variables for sync single user */
export type SyncUserPathVars = z.infer<typeof SyncUserPathVarsSchema>;

/** Single user sync response */
export type SyncUserResponse = z.infer<typeof SyncUserResponseSchema>;
